<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4" :loading="form.loading">
          <v-card-title class="text-indigo text-button">
            {{ editing ? "Modificar usuario" : "Crear usuario" }}
          </v-card-title>

          <v-form ref="formRef" @submit.prevent="saveUser">
            <v-container fluid>
              <v-row>
                <!-- ── Datos institucionales ─────────────────────────────── -->
                <v-col cols="12">
                  <h4 class="text-button text-primary">Datos Institucionales</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col v-if="editing" cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="form.loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          :model-value="form.data.id"
                          label="ID"
                          prepend-inner-icon="mdi-identifier"
                          readonly
                          hint="Identificador interno, asignado por el sistema."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="form.loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="form.data.username"
                          label="Nombre de usuario"
                          prepend-inner-icon="mdi-account"
                          :disabled="form.saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('username')"
                          hint="Debe coincidir con el identificador registrado en el proveedor de identidad: es contra este valor que la API resuelve el token."
                          persistent-hint
                          @update:model-value="errors.clear('username')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="form.loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="form.data.email"
                          label="Email"
                          prepend-inner-icon="mdi-email"
                          :disabled="form.saving"
                          :rules="[rules.required, rules.email]"
                          :error-messages="errors.for('email')"
                          hint="Puede ser el email institucional o uno personal."
                          persistent-hint
                          @update:model-value="errors.clear('email')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <!-- ── Datos de firma ────────────────────────────────────── -->
                <v-col cols="12">
                  <h4 class="text-button text-primary">Datos de Firma</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="form.loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="form.data.firstname"
                          label="Nombres"
                          prepend-inner-icon="mdi-account-edit"
                          :disabled="form.saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('firstname')"
                          @update:model-value="errors.clear('firstname')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="form.loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="form.data.lastname"
                          label="Apellidos"
                          prepend-inner-icon="mdi-account-edit"
                          :disabled="form.saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('lastname')"
                          @update:model-value="errors.clear('lastname')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="form.loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="form.data.dni"
                          label="RUT"
                          prepend-inner-icon="mdi-card-account-details"
                          :disabled="form.saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('dni')"
                          hint="Sin puntos ni guión."
                          persistent-hint
                          @update:model-value="errors.clear('dni')"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="form.loading" type="list-item-two-line" />
                        <v-file-input
                          v-else
                          v-model="signatureFile"
                          accept=".jpg,.jpeg,.png"
                          label="Rúbrica (opcional)"
                          show-size
                          prepend-icon=""
                          prepend-inner-icon="mdi-draw"
                          placeholder="Haz clic para seleccionar la imagen"
                          hint="Extensión .jpg, .jpeg o .png."
                          persistent-hint
                          :disabled="form.saving"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4" xl="3">
                        <v-skeleton-loader v-if="form.loading" type="image" />
                        <v-sheet v-else-if="form.data.signature" elevation="1" class="pa-2">
                          <!-- <img> plano y no <v-img>: ver la nota en AgAppBar.vue. -->
                          <img
                            :src="form.data.signature"
                            alt="Rúbrica"
                            style="width: 100%; height: auto; display: block"
                          />
                        </v-sheet>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <!-- ── Acceso ────────────────────────────────────────────── -->
                <v-col cols="12" lg="6">
                  <h4 class="text-button text-primary">Acceso</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-skeleton-loader v-if="form.loading" type="button" />
                    <v-switch
                      v-else
                      v-model="form.data.state"
                      color="success"
                      inset
                      true-value="ACTIVE"
                      false-value="INACTIVE"
                      :disabled="form.saving"
                      :label="form.data.state === 'ACTIVE' ? 'Activado' : 'Desactivado'"
                      hint="Un usuario desactivado no podrá ingresar al sistema ni firmar con sus certificados."
                      persistent-hint
                    />
                  </v-container>
                </v-col>

                <!-- ── Privilegios ───────────────────────────────────────── -->
                <v-col cols="12" lg="6">
                  <h4 class="text-button text-primary">Privilegios</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-skeleton-loader v-if="form.loading" type="list-item-three-line" />
                    <v-select
                      v-else
                      v-model="form.data.roles"
                      :items="roleItems"
                      :item-props="true"
                      label="Roles (opcional)"
                      multiple
                      chips
                      clearable
                      :disabled="form.saving"
                      :error-messages="errors.for('roles')"
                      hint="El rol Admin ya incluye todos los privilegios, así que se selecciona solo."
                      persistent-hint
                    />
                  </v-container>
                </v-col>
              </v-row>
            </v-container>

            <v-card-actions>
              <v-spacer />
              <v-btn
                type="submit"
                variant="text"
                color="success"
                class="mx-1"
                :loading="form.saving"
                :disabled="form.loading"
              >
                <v-icon class="mr-1">mdi-content-save</v-icon>Guardar
              </v-btn>
              <v-btn
                variant="text"
                color="error"
                class="mx-1"
                :disabled="form.saving"
                @click="router.push('/admin/users')"
              >
                Cancelar
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { UserRoles } from "bancojs";
import type { PersonCreatePayload, UserRole } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useFormErrors } from "@/composables/useFormErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { roleLabel } from "@/utils/labels";

const route = useRoute();
const router = useRouter();
const { report } = useApiErrors();
const notifications = useNotificationsStore();
const errors = useFormErrors();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const editing = computed(() => !!route.params.id);
/** Sólo existe al editar; en alta lo asigna la base de datos. */
const userId = computed(() => Number(route.params.id));

/**
 * El formulario anterior pedía `id` con la etiqueta "Identificador de Usuario" y
 * la nota de que debía coincidir con el username del directorio institucional.
 * Eso era un resabio de la semántica v1: en la API actual `id` es la clave
 * primaria numérica que asigna la base, y **`username`** es el valor contra el
 * que RolesClaimsAdapter resuelve el claim del token. Además ese `id` se enviaba
 * en el POST de creación, donde la API lo ignora.
 *
 * Ahora `id` se muestra sólo lectura al editar, y la nota está donde corresponde.
 */
const form = ref({
  data: {
    username: "",
    name: "",
    email: "",
    state: "ACTIVE",
    roles: [] as UserRole[],
    firstname: "",
    lastname: "",
    dni: "",
    signature: "",
    id: undefined as number | undefined,
  },
  loading: true,
  saving: false,
});

const signatureFile = ref<File | File[] | null>(null);

const rules = {
  required: (val: unknown) => !!val || "Campo requerido.",
  email: (val: unknown) => /.+@.+/.test(String(val ?? "")) || "Email inválido.",
};

/**
 * `:item-props="true"` hace que Vuetify expanda la clave `props` de cada item,
 * que es como se deshabilitan opciones en V3+ (`item-disabled` desapareció).
 */
const roleItems = computed(() =>
  UserRoles.map((role) => ({
    title: roleLabel(role),
    value: role,
    props: {
      // Con ADMIN seleccionado el resto es redundante: ya incluye todo.
      disabled: role !== "ADMIN" && form.value.data.roles.includes("ADMIN"),
    },
  }))
);

// Seleccionar ADMIN colapsa la selección: los demás roles son subconjuntos.
watch(
  () => form.value.data.roles,
  (roles) => {
    if (roles.length > 1 && roles.includes("ADMIN")) {
      form.value.data.roles = ["ADMIN"];
    }
  }
);

watch(signatureFile, async (file) => {
  const picked = Array.isArray(file) ? file[0] : file;
  if (!picked) {
    form.value.data.signature = "";
    return;
  }
  try {
    form.value.data.signature = await toBase64(picked);
  } catch {
    notifications.error("No fue posible leer la imagen de la rúbrica.", "Rúbrica");
  }
});

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function load() {
  if (!editing.value) {
    form.value.loading = false;
    return;
  }
  try {
    const person = await banco.admin.users.get(userId.value);
    Object.assign(form.value.data, person);
  } catch (err) {
    report(err, {
      title: "Usuario",
      fallback: "Ocurrió un error al recuperar la información del usuario.",
    });
  } finally {
    form.value.loading = false;
  }
}

async function saveUser() {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;

  errors.reset();
  form.value.saving = true;

  // El nombre visible se compone de nombres y apellidos, igual que antes.
  const payload: PersonCreatePayload = {
    username: form.value.data.username,
    name: `${form.value.data.firstname} ${form.value.data.lastname}`.trim(),
    email: form.value.data.email,
    state: form.value.data.state as "ACTIVE" | "INACTIVE",
    roles: form.value.data.roles,
    firstname: form.value.data.firstname,
    lastname: form.value.data.lastname,
    dni: form.value.data.dni,
    signature: form.value.data.signature || undefined,
  };

  try {
    if (editing.value) {
      await banco.admin.users.update(userId.value, payload);
      notifications.success("Se guardaron los cambios en el usuario.", "Guardado");
    } else {
      await banco.admin.users.create(payload);
      notifications.success("Se creó el usuario exitosamente.", "Creado");
    }
    router.push("/admin/users");
  } catch (err) {
    // `form: errors` es lo que pinta un 422 sobre los campos en vez de dejar al
    // usuario con un mensaje genérico.
    report(err, {
      title: "Usuario",
      fallback: "Ocurrió un error al guardar el usuario.",
      form: errors,
    });
  } finally {
    form.value.saving = false;
  }
}

onMounted(load);
</script>
